/**
 * The scan surface's own state: a batch of captures and what the cascade said
 * about each one.
 *
 * Deliberately plain data with pure helpers rather than a reducer soup, because
 * the interesting decisions are the API's and the core's. This holds what came
 * back, tallies it, and turns an accepted item into the scan body the intake route
 * expects. Nothing here decides which printing a card is.
 *
 * Two lists come out of one batch, and the split is the product:
 *
 *  - **ready** - the cascade committed, or a human resolved a review. These are
 *    what a commit sends, as one batch, because that is how an offline queue
 *    replays and how the route was built: one transaction, one slot per card.
 *  - **review** - low confidence, a shared illustration, or a picture that matched
 *    nothing. Intake does not stop for these; they wait for a human, with the
 *    cascade's own candidates and the cascade's own explanation of why.
 */

import {
  type FrameClass,
  type MatchOutcome,
  type RecognitionCandidate,
  type ResolveReport,
} from '@unsorted/shared';
import type { CopyLocation, ScanEvidence, ScanRequest } from '../api/types.js';

export type ScanStatus =
  /** Decoding and recognising. */
  | 'reading'
  /** The cascade committed, or a human chose the printing: it will be filed. */
  | 'ready'
  /** The cascade would not decide. It needs a person. */
  | 'review'
  /** The picture matched nothing at all. Also a person, with the distance named. */
  | 'failed'
  /** No card in the capture: retake it, or file the card by hand. */
  | 'refused'
  /** The API could not be reached or refused: retryable. */
  | 'error'
  /** Filed: the copy exists and the address is known. */
  | 'filed'
  /** The scan id was already committed: the same card, already on a shelf. */
  | 'duplicate';

export interface ChosenPrinting {
  printingId: number;
  name: string;
  setCode: string;
  collectorNumber: string;
}

export interface ScanItem {
  /** Client-minted and stable: the route's idempotency key. */
  scanId: string;
  addedAt: string;
  /** What the operator called it - a file name, or "capture 3". */
  label: string;
  status: ScanStatus;
  frameClass: FrameClass;
  artPhash: string | null;
  /** The cascade's own confidence, 0 when nothing was read. */
  confidence: number;
  /** What will be filed, once the cascade or a human has agreed. */
  chosen: ChosenPrinting | null;
  /** The cascade's whole report, kept so the evidence and the explanation survive. */
  report: ResolveReport | null;
  candidates: RecognitionCandidate[];
  /** The cascade's notes, in order, for the explanation under a review item. */
  notes: string[];
  /** A sentence for the operator: the failure, the warning, the address. */
  message: string | null;
  /** Where the copy went, once the commit answered. */
  location: CopyLocation | null;
}

export interface BatchCounts {
  ready: number;
  review: number;
  filed: number;
  reading: number;
  refused: number;
  failed: number;
  error: number;
}

/** The items a commit will send, in the order they were captured. */
export const readyItems = (items: readonly ScanItem[]): ScanItem[] => items.filter((item) => item.status === 'ready');

/** The items waiting for a human. */
export const reviewItems = (items: readonly ScanItem[]): ScanItem[] => items.filter((item) => item.status === 'review' || item.status === 'failed');

export function countBatch(items: readonly ScanItem[]): BatchCounts {
  const counts: BatchCounts = { ready: 0, review: 0, filed: 0, reading: 0, refused: 0, failed: 0, error: 0 };
  for (const item of items) {
    if (item.status === 'ready') counts.ready += 1;
    else if (item.status === 'review') counts.review += 1;
    else if (item.status === 'failed') counts.failed += 1;
    else if (item.status === 'filed' || item.status === 'duplicate') counts.filed += 1;
    else if (item.status === 'reading') counts.reading += 1;
    else if (item.status === 'refused') counts.refused += 1;
    else if (item.status === 'error') counts.error += 1;
  }
  return counts;
}

/** The cascade's outcome, as this surface's status. */
export function statusForOutcome(outcome: MatchOutcome, hasChosen: boolean): ScanStatus {
  if (hasChosen) return 'ready';
  if (outcome === 'auto_committed' || outcome === 'manual') return 'ready';
  return outcome === 'failed' ? 'failed' : 'review';
}

/** A candidate, or a human's pick, as the thing that will be filed. */
export function toChosenPrinting(source: {
  printingId: number;
  name?: string;
  setCode?: string;
  collectorNumber?: string;
}): ChosenPrinting {
  return {
    printingId: source.printingId,
    name: source.name ?? `Printing ${source.printingId}`,
    setCode: source.setCode ?? '',
    collectorNumber: source.collectorNumber ?? '',
  };
}

/** "Alarum (MIR 2)" - how a printing is named in a list a human reads. */
export const printingLabel = (printing: { name: string; setCode: string; collectorNumber: string }): string =>
  `${printing.name} (${printing.setCode.toUpperCase()} ${printing.collectorNumber})`;

/**
 * What the device read, as the intake route records it.
 *
 * The evidence is sent whenever there was a reading, including when a human
 * overrode the pick: the point of the trail is what the device saw *and* what was
 * filed, and the route keeps `chosen_printing_id` beside the copy's printing id
 * for exactly that. A card typed in by hand carries no evidence, because claiming
 * a distance nothing measured would put invented numbers in the table the
 * recognition modes are judged by.
 */
export function evidenceFor(item: ScanItem): ScanEvidence | null {
  if (item.report === null || item.chosen === null) return null;
  const report: ResolveReport = item.report;
  return {
    frameClass: item.frameClass,
    ...(report.art === null ? {} : { artDistance: report.art.distance }),
    candidateCount: report.candidates.length,
    chosenPrintingId: item.chosen.printingId,
    confidence: report.confidence,
  };
}

/** One item, as the commit body. */
export function scanRequestBody(item: ScanItem): ScanRequest {
  if (item.chosen === null) throw new Error(`${item.label} has no printing to file`);
  return {
    scanId: item.scanId,
    printingId: item.chosen.printingId,
    finish: 'nonfoil',
    condition: 'near_mint',
    lang: 'en',
    source: 'scan',
    evidence: evidenceFor(item),
  };
}

/** A fresh client-minted scan id: the route refuses a scan without a UUID. */
export function newScanId(): string {
  const cryptoLike = globalThis.crypto as { randomUUID?: () => string } | undefined;
  if (cryptoLike?.randomUUID) return cryptoLike.randomUUID();
  // Only reached where `crypto.randomUUID` is missing (older webviews). The route
  // validates the shape, so a fallback still has to look like a UUID.
  const hex = (length: number): string =>
    Array.from({ length }, () => Math.floor(Math.random() * 16).toString(16)).join('');
  return `${hex(8)}-${hex(4)}-4${hex(3)}-a${hex(3)}-${hex(12)}`;
}
